/* eslint-disable no-console */

import PropTypes from 'prop-types'
import classnames from 'classnames'
import {Map} from 'immutable'
import {useEffect, useMemo, useState} from 'react'
import {connect} from 'react-redux'
import {readEndpoint, createResource} from 'redux-json-api'
import {withRouter} from 'react-router'

import DashboardHeading from './mui_base_components/dashboard_heading/DashboardHeading'
import DownloadIcon from '../shared_components/DownloadIcon'
import ItemResponseGroupInformation from './item_response_groups/ItemResponseGroupInformation'
import MixpanelLogger from '../lib/MixpanelLogger'
import PdfViewerController from '../shared_components/PdfViewerController'
import Button from '../shared_components/mui_base_components/button/Button'
import PrearrangedFuneralModal from '../shared_components/prearranged_funeral_modal/PrearrangedFuneralModal'
import PreviewIcon from './svg_icons/icons/PreviewIcon'

import {isDeferredReadResponseGroup} from '../lib/deputyPermissionTools'
import {addPossessive} from '../lib/tools'

import {downloadWithFetch} from '../lib/fileDownload'

import {cleanResponse} from '../dashboard/src/components/corp_max/redux/apiHelper'

import {useNotificationContext} from './notifications/NotificationContext'

import {
  findFirmConfigByFirmOwnership,
  findRawResourceByAttributeId,
  findRawResourceById
} from '../lib/plan_data/userData'

import {currentUserConfig, currentUser} from '../lib/userTools'
import routerUtils from '../lib/routerUtils'

import './prearrangedFuneralCard.scss'

export const PrearrangedFuneralCard = props => {
  const {
    advisorContactCard,
    clientName,
    deputyOwnership = Map(),
    everplanId,
    firm,
    isDeputyUpgradePromptVisible,
    isOwnEverplan,
    showConfirmationMessage,
    vaultExtension,
    pdfDownloadName,
    location,
    userConfig
  } = props

  const firstGroupId = useMemo(() => vaultExtension.get('id'), [vaultExtension])

  const {alwaysNotify} = useNotificationContext()

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isPdfViewerOpen, setIsPdfViewerOpen] = useState(false)
  const [pdfName, setPdfName] = useState('')
  const [pdfUrl, setPdfUrl] = useState('')
  const [isMemorialGuideDownloading, setIsMemorialGuideDownloading] = useState(false)

  // Check if we should open the modal from query parameter
  useEffect(() => {
    if (location && location.query && location.query.openVaultExtensionModal === 'true') {
      setIsModalOpen(true)
      // eslint-disable-next-line no-unused-vars
      const {openVaultExtensionModal, ...remainingQuery} = location.query
      routerUtils.replace({
        ...location,
        query: remainingQuery
      })
    }
  }, [location])

  useEffect(() => {
    if (isModalOpen)
      MixpanelLogger.track('view_vault_extension')

    if (isModalOpen && props.actor === 'deputy') {
      const payload = {
        type: 'viewed-items',
        attributes: {
          'user-id': props.userConfig.get('id'),
          'everplan-id': props.vaultExtension.get('everplan-id'),
          'item-id': props.vaultExtension.get('id'),
          data: {
            'group-id': [props.vaultExtension.get('id')]
          }
        }
      }

      props.createResource(payload)
    }
  }, [isModalOpen])

  if (vaultExtension.isEmpty())
    return null

  const funeralHomeName = vaultExtension.getIn(['firm', 'name']) ||
    firm?.get('name') ||
    advisorContactCard?.get('firm-name') ||
    ''

  const title = isOwnEverplan ?
    'My Prearrangements' :
    `${clientName ? `${addPossessive(clientName)} ` : ''}Prearrangements`

  const generatePdfFilename = () => {
    if (!pdfDownloadName) return 'prearranged-funeral-plan.pdf'

    const createdAt = vaultExtension.get('created-at')
    const date = createdAt ? new Date(createdAt) : new Date()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    const year = date.getFullYear()
    const formattedDate = `${month}-${day}-${year}`
    const sanitizedFileName = pdfDownloadName.replace(/[^a-zA-Z0-9]/g, '-')

    return `${sanitizedFileName}-${formattedDate}.pdf`
  }

  const fetchPresignedUrl = () => {
    const vaultExtensionId = vaultExtension.get('id')

    if (!vaultExtensionId) {
      alwaysNotify.shortError('No vault extension ID available for download')

      return Promise.reject(new Error('No vault extension ID'))
    }

    return props.readEndpoint(
      `vault-extension-download-presigners/${vaultExtensionId}`
    )
      .then(response => {
        const presignedUrl = response.body.data.attributes['completed-pdf-presigned-url'] ||
          response.body.data.attributes['goods-and-services-pdf-presigned-url']

        if (!presignedUrl) {
          alwaysNotify.shortError('No presigned URL received for download')

          return Promise.reject(new Error('No presigned URL'))
        }

        return presignedUrl
      })
      .catch(error => {
        alwaysNotify.shortError('Failed to prepare download. Please try again.')

        console.error('Failed to get presigned URL for download:', error)

        return Promise.reject(error)
      })
  }

  const handleButtonClick = () => {
    MixpanelLogger.track('click_vault_extension_module')

    setIsModalOpen(true)
  }

  const handleCloseModal = () => setIsModalOpen(false)

  const handleClosePdfViewerModal = () => {
    setIsPdfViewerOpen(false)
    setPdfName('')
    setPdfUrl('')
  }

  const handleDownload = () => {
    MixpanelLogger.track('click_download_vault_extension_attachment')

    const filename = generatePdfFilename()

    fetchPresignedUrl()
      .then(presignedUrl => {
        alwaysNotify.shortSuccess('The documents are being downloaded.')

        return downloadWithFetch(presignedUrl, filename)
      })
      .catch(error => {
        if (error.message !== 'No vault extension ID' &&
          error.message !== 'No presigned URL') {
          alwaysNotify.shortError('Failed to download file. Please try again.')

          console.error('Download failed:', error)
        }
      })
  }

  const handleMemorialGuideDownload = () => {
    if (isMemorialGuideDownloading || !props.memorialGuideDownload.isEmpty()) {
      alwaysNotify.shortInfo(
        'Only one memorial guide can be downloaded at a time. Please try again.'
      )
      return
    }

    setIsMemorialGuideDownloading(true)
    MixpanelLogger.track('click_download_memorial_guide')

    props.createResource({
      type: 'memorial-guide-downloads',
      attributes: {'everplan-id': everplanId}
    })
      .then(() => {
        alwaysNotify.shortInfo(
          'Preparing your memorial guide download. This may take a few minutes.'
        )
      })
      .catch(() => {
        alwaysNotify.shortError('Failed to start memorial guide download. Please try again.')
      }).finally(() => setIsMemorialGuideDownloading(false))
  }

  const handlePdfPreview = () => {
    const filename = generatePdfFilename()

    setPdfName(filename)

    fetchPresignedUrl()
      .then(presignedUrl => {
        setPdfUrl(presignedUrl)
        setIsPdfViewerOpen(true)
      })
      .catch(error => {
        alwaysNotify.shortError('Failed to preview PDF. Please try again.')

        console.error('PDF preview failed:', error)
      })
  }

  const actionButtonsAndLockedStatusElements = (
    <>
      <div className='action-buttons'>
        <Button
          className='view-plan-button'
          onClick={handleButtonClick}
          color='primary'
          size='medium'
          variant='contained'>
          View plan
        </Button>
        <a className='download-link' onClick={handlePdfPreview}>
          <PreviewIcon />
          Policy Details
        </a>
        {
          // An owner downloads the guide to their own prearrangement, and a
          // firm user downloads it for the everplans they advise.
          (isOwnEverplan || (userConfig.get('firm-id') && !userConfig.get('client'))) && (
            <a className='download-link memorial-guide' onClick={handleMemorialGuideDownload}>
              <DownloadIcon />
              Memorial Guide
            </a>
          )
        }
      </div>
      {
        props.isPreviewPage &&
        !props.isOwnEverplan &&
        isDeferredReadResponseGroup({deputyOwnership, firstGroupId}) &&
        <div className='locked-status'>
          <ItemResponseGroupInformation
            context='everplan_preview'
            deputyOwnership={deputyOwnership}
            firstGroupId={firstGroupId}
            ownEverplan={isOwnEverplan}
          />
        </div>
      }
    </>
  )

  return (
    <div
      className='prearranged-funeral-card'
      data-testid='prearranged-funeral-card'>
      {
        props.context === 'dashboard' &&
        <DashboardHeading>Your prearrangements</DashboardHeading>
      }
      <div className='prearranged-funeral-card-box'>
        <p className='prearranged-funeral-card-eyebrow'>Provided by {funeralHomeName}</p>
        <div className='prearranged-funeral-card-title-and-content-container'>
          <h2 className='prearranged-funeral-card-title'>{title}</h2>
          {
            showConfirmationMessage &&
            <p className='confirmation-message'>
              Congratulations. You've prearranged your funeral.
            </p>
          }
          <div
            className={classnames(
              'action-buttons-and-locked-status-container',
              {'with-deputy-upgrade-prompt': isDeputyUpgradePromptVisible}
            )}>
            {actionButtonsAndLockedStatusElements}
          </div>
        </div>
      </div>
      <PrearrangedFuneralModal
        {...props}
        clientName={clientName}
        everplanId={everplanId}
        firm={firm}
        isOwnEverplan={isOwnEverplan}
        showClientText={isOwnEverplan}
        onClose={handleCloseModal}
        onMemorialGuideClick={handleMemorialGuideDownload}
        onPolicyDetailsClick={handlePdfPreview}
        open={isModalOpen}
        vaultExtensionData={vaultExtension.get('data')}
        vaultExtensionPolicyInfo={vaultExtension.get('policy-info')}
      />
      {isPdfViewerOpen && pdfUrl && (
        <PdfViewerController
          isModalOpen={isPdfViewerOpen}
          onCloseModal={handleClosePdfViewerModal}
          onDownloadClick={handleDownload}
          pdfName={pdfName}
          pdfUrl={pdfUrl}
        />
      )}
    </div>
  )
}

PrearrangedFuneralCard.propTypes = {
  actor: PropTypes.string,
  advisorContactCard: PropTypes.instanceOf(Map),
  clientName: PropTypes.string,
  context: PropTypes.string,
  createResource: PropTypes.func,
  deputyOwnership: PropTypes.instanceOf(Map),
  everplanId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  firm: PropTypes.instanceOf(Map),
  firmConfig: PropTypes.instanceOf(Map),
  isDeputyUpgradePromptVisible: PropTypes.bool,
  isOwnEverplan: PropTypes.bool,
  location: PropTypes.object,
  memorialGuideDownload: PropTypes.instanceOf(Map),
  pdfDownloadName: PropTypes.string,
  readEndpoint: PropTypes.func,
  showConfirmationMessage: PropTypes.bool,
  isPreviewPage: PropTypes.bool,
  vaultExtension: PropTypes.instanceOf(Map),
  userConfig: PropTypes.instanceOf(Map)
}

PrearrangedFuneralCard.defaultProps = {
  firm: Map(),
  memorialGuideDownload: Map(),
  vaultExtension: Map(),
  showConfirmationMessage: true,
  isPreviewPage: false
}

const mapStateToProps = (state, {everplanId, firmId, clientName}) => {
  const userConfig = currentUserConfig(state.api)
  const pdfDownloadName = everplanId ?
    clientName :
    (() => {
      const user = currentUser({userConfig, users: state.api.users})
      return `${user.get('first-name') } ${ user.get('last-name')}`
    })()

  const currentEverplanId = everplanId || userConfig.get('everplan-id')
  const vaultExtension = findRawResourceByAttributeId({
    rawResource: state.api['vault-extensions'],
    attribute: 'everplan-id',
    id: currentEverplanId
  })
  const firmOwnership = findRawResourceByAttributeId({
    rawResource: state.api['firm-ownerships'],
    attribute: 'everplan-id',
    id: currentEverplanId
  })

  const currentFirmId = firmId || firmOwnership.get('ownable-id')
  const firm = findRawResourceById({
    rawResource: state.api.firms,
    id: currentFirmId
  })

  // Calculate if this is the user's own everplan
  const vaultExtensionEverplanId = vaultExtension.get('everplan-id')
  const isOwnEverplan = userConfig.get('everplan-id') === vaultExtensionEverplanId

  return ({
    vaultExtension,
    firm,
    firmConfig: findFirmConfigByFirmOwnership({firmOwnerships: state.api['firm-ownerships'], firmConfigs: state.api['firm-configs'], userConfig}),
    everplanId: currentEverplanId,
    isOwnEverplan,
    memorialGuideDownload: cleanResponse(state.api['memorial-guide-downloads'])
      .sortBy(download => download.get('created-at'))
      .last() || Map(),
    pdfDownloadName,
    userConfig
  })
}

export default withRouter(connect(mapStateToProps, {readEndpoint, createResource})(PrearrangedFuneralCard))
